import pointInPolygon from 'point-in-polygon';

/**
 * Calculate the centroid (geometric center) of a polygon
 * @param {Array} polygon - Array of [x, y] coordinate pairs (normalized 0-1)
 * @returns {Array} [x, y] centroid coordinates
 */
export const calculatePolygonCentroid = (polygon) => {
    if (!polygon || polygon.length < 3) {
        return [0, 0];
    }

    let area = 0;
    let x = 0;
    let y = 0;
    const n = polygon.length;

    for (let currentPoint = 0; currentPoint < n; currentPoint++) {
        const nextPoint = (currentPoint + 1) % n;
        const xCurrent = polygon[currentPoint][0];
        const yCurrent = polygon[currentPoint][1];
        const xNext = polygon[nextPoint][0];
        const yNext = polygon[nextPoint][1];

        const a = xCurrent * yNext - xNext * yCurrent;
        area += a;
        x += (xCurrent + xNext) * a;
        y += (yCurrent + yNext) * a;
    }

    area *= 0.5;
    if (area === 0) {
        // Fallback to simple average for degenerate polygons
        return [
            polygon.reduce((sum, point) => sum + point[0], 0) / n,
            polygon.reduce((sum, point) => sum + point[1], 0) / n,
        ];
    }

    return [x / (6 * area), y / (6 * area)];
};

/**
 * Calculate the distance between two points
 * @param {Array} point1 - [x, y] coordinates
 * @param {Array} point2 - [x, y] coordinates
 * @returns {number} Distance between points
 */
export const calculateDistance = (point1, point2) => {
    const dx = point1[0] - point2[0];
    const dy = point1[1] - point2[1];
    return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Check if a point is inside a polygon
 * @param {Array} point - [x, y] coordinates (normalized 0-1)
 * @param {Array} polygon - Array of [x, y] coordinate pairs (normalized 0-1)
 * @returns {boolean} True if point is inside polygon
 */
export const isPointInPolygon = (point, polygon) => {
    return pointInPolygon(point, polygon);
};

/**
 * Find the best ROI for a pin based on the design requirements:
 * 1. If pin is inside any ROIs, choose the one with closest centroid
 * 2. If pin is not inside any ROI, find the ROI with closest centroid
 * @param {Object} pin - Pin object with xPct, yPct properties
 * @param {Array} rois - Array of ROI objects with polygon and name properties
 * @returns {Object|null} Best matching ROI or null if no ROIs available
 */
export const findBestROIForPin = (pin, rois) => {
    if (!pin || !rois || rois.length === 0) {
        return null;
    }

    const pinPoint = [pin.xPct, pin.yPct];

    // First, find all ROIs that contain the pin
    const containingROIs = rois.filter((roi) => {
        const hasPolygon = roi.polygon && Array.isArray(roi.polygon);

        return hasPolygon && isPointInPolygon(pinPoint, roi.polygon);
    });

    // If pin is inside one or more ROIs, find the one with closest centroid
    if (containingROIs.length > 0) {
        let closestROI = containingROIs[0];
        let minDistance = calculateDistance(pinPoint, calculatePolygonCentroid(closestROI.polygon));

        for (let i = 1; i < containingROIs.length; i++) {
            const distance = calculateDistance(
                pinPoint,
                calculatePolygonCentroid(containingROIs[i].polygon)
            );
            if (distance < minDistance) {
                minDistance = distance;
                closestROI = containingROIs[i];
            }
        }

        return closestROI;
    }

    // If pin is not inside any ROI, find the ROI with closest centroid
    let closestROI = rois[0];
    let minDistance = calculateDistance(pinPoint, calculatePolygonCentroid(closestROI.polygon));

    for (let i = 1; i < rois.length; i++) {
        const distance = calculateDistance(pinPoint, calculatePolygonCentroid(rois[i].polygon));
        if (distance < minDistance) {
            minDistance = distance;
            closestROI = rois[i];
        }
    }

    return closestROI;
};

/**
 * Validate if a polygon is valid (has at least 3 points and is not self-intersecting)
 * @param {Array} polygon - Array of [x, y] coordinate pairs
 * @returns {boolean} True if polygon is valid
 */
export const isValidPolygon = (polygon) => {
    if (!polygon || polygon.length < 3) {
        return false;
    }

    // Check if all coordinates are within valid range (0-1)
    return polygon.every(
        (point) =>
            Array.isArray(point) &&
            point.length === 2 &&
            point[0] >= 0 &&
            point[0] <= 1 &&
            point[1] >= 0 &&
            point[1] <= 1
    );
};

/**
 * Convert normalized coordinates to pixel coordinates
 * @param {Array} point - [x, y] normalized coordinates (0-1)
 * @param {Object} imageSize - {width, height} in pixels
 * @returns {Array} [x, y] pixel coordinates
 */
export const normalizedToPixel = (point, imageSize) => {
    return [point[0] * imageSize.width, point[1] * imageSize.height];
};

/**
 * Convert pixel coordinates to normalized coordinates
 * @param {Array} point - [x, y] pixel coordinates
 * @param {Object} imageSize - {width, height} in pixels
 * @returns {Array} [x, y] normalized coordinates (0-1)
 */
export const pixelToNormalized = (point, imageSize) => {
    return [point[0] / imageSize.width, point[1] / imageSize.height];
};
